/**
 * Builds the V3 `SearchListingsRequest` for the SRP from the same inputs the V2
 * `buildSrpListingsParams` used (area, boundary/viewport bounds, URL filters,
 * page). The map viewport is sent in `filters.boundingBox`; the slug is sent as
 * `searchSlug` (the BE applies its slug defaults). This builder is shared by the
 * server fetcher and the client live-query, so it stays framework-neutral.
 */
import type { HotpadsApiAreaType, HpBoundingBoxDto, HpSearchScopeDto } from '@zg-rentals/ts-hotpads-api-client';
import type { AreaResponse, BoundaryData, SearchListingsRequest } from '@/src/lib/api';
import { calculateCenterAndZoomFromBoundary } from '@/src/components/features/search/Map/calculateCenterAndZoomFromBoundary';
import { BASE_SEARCH_SLUG } from '@/src/lib/api/endpoints/search';
import { LISTINGS_PER_PAGE } from '@/src/lib/constants';
import { filterStateToV3Filter } from '@/src/lib/filters/filterStateToV3';
import { DEFAULT_FILTERS, type FilterState } from '@/src/lib/filters/filterTypes';
import { type MapPositionFromViewport, normalizeBoundsForListingQuery } from '@/src/lib/map/viewportBounds';
import { SRP_LISTINGS_VIEW } from './srpListingsView';

type SearchBoundingBox = HpBoundingBoxDto;

export type SrpSearchRequestArgs = {
  areaData: AreaResponse;
  boundary: BoundaryData | null;
  page: number;
  filterState: FilterState;
  searchSlug: string;
  /**
   * Explicit map viewport from the URL (`?lat&lon&z`) or live map state. When
   * set it wins; otherwise we fit to the area boundary, falling back to the
   * area envelope — mirroring `buildSrpListingsParams`.
   */
  initialMapPosition?: MapPositionFromViewport | null;
  /**
   * "Area Border" on → clip results to the scope. Off → search
   * the raw viewport. Defaults to true (V2's default border-on behavior).
   */
  showBorder?: boolean;
};

export function computeSrpBoundingBox(
  areaData: AreaResponse,
  boundary: BoundaryData | null,
  initialMapPosition?: MapPositionFromViewport | null,
): SearchBoundingBox {
  let bounds = {
    minLat: initialMapPosition?.bounds.southLat ?? areaData.minLat!,
    maxLat: initialMapPosition?.bounds.northLat ?? areaData.maxLat!,
    minLon: initialMapPosition?.bounds.westLng ?? areaData.minLon!,
    maxLon: initialMapPosition?.bounds.eastLng ?? areaData.maxLon!,
  };

  if (!initialMapPosition && boundary?.coordinates) {
    const position = calculateCenterAndZoomFromBoundary(
      boundary.coordinates,
      SRP_LISTINGS_VIEW.width,
      SRP_LISTINGS_VIEW.height,
      SRP_LISTINGS_VIEW.padding,
    );
    if (position) {
      bounds = {
        minLat: position.bounds.southLat,
        maxLat: position.bounds.northLat,
        minLon: position.bounds.westLng,
        maxLon: position.bounds.eastLng,
      };
    }
  }

  const normalized = normalizeBoundsForListingQuery({
    southLat: bounds.minLat,
    northLat: bounds.maxLat,
    westLng: bounds.minLon,
    eastLng: bounds.maxLon,
  });

  return {
    minLat: normalized.southLat,
    maxLat: normalized.northLat,
    minLon: normalized.westLng,
    maxLon: normalized.eastLng,
  };
}

export function buildSrpSearchRequest({
  areaData,
  boundary,
  page,
  filterState,
  searchSlug,
  initialMapPosition,
  showBorder = true,
}: SrpSearchRequestArgs): SearchListingsRequest {
  const boundingBox = computeSrpBoundingBox(areaData, boundary, initialMapPosition);
  const clipScope = showBorder && areaData.type !== 'university' ? areaData.scope : undefined;

  return {
    includeBreadcrumbs: true,
    searchSlug,
    area: {
      resourceId: areaData.resourceId ?? '',
      name: areaData.name ?? '',
      type: (areaData.type ?? 'city') as HotpadsApiAreaType,
      city: areaData.city ?? '',
      state: areaData.state ?? '',
    },
    filters: buildSrpFilters(filterState, boundingBox, clipScope),
    page: { number: page, limit: LISTINGS_PER_PAGE },
  };
}

export function buildSrpFilters(filterState: FilterState, boundingBox?: SearchBoundingBox, scope?: HpSearchScopeDto) {
  return {
    ...filterStateToV3Filter(filterState, boundingBox),
    ...(scope && { scope }),
  };
}

const PROBE_PAGE = { number: 1, limit: 1 } as const;

/**
 * A count-only variant of `base` carrying exactly `filterState`, for measuring
 * what one filter costs on a zero-result SRP.
 *
 * Anchored on the neutral base slug: the SRP's own slug would re-impose the
 * filter being measured. `orderBy` is dropped so a sort change does not re-key
 * every probe. Shared with the server prefetch, which must derive a
 * byte-identical request or its count lands under a different query key.
 */
export function buildFilterProbeRequest(base: SearchListingsRequest, filterState: FilterState): SearchListingsRequest {
  const boundingBox = (base.filters?.boundingBox ?? undefined) as SearchBoundingBox | undefined;
  return {
    ...base,
    includeBreadcrumbs: false,
    searchSlug: BASE_SEARCH_SLUG,
    filters: buildSrpFilters({ ...filterState, orderBy: null }, boundingBox, base.filters?.scope),
    page: PROBE_PAGE,
  };
}

/** Listings the same viewport holds with every filter cleared. */
export function buildUnfilteredCountRequest(base: SearchListingsRequest): SearchListingsRequest {
  return buildFilterProbeRequest(base, DEFAULT_FILTERS);
}
